import { useState, useEffect } from 'react';

import { WantedPersonApi } from '@/api';
import { IWantedPerson } from '@/models/wanted.person';

interface UseWantedPersonStatusReturn {
  isWanted: boolean;
  wantedRecords: IWantedPerson[];
  loading: boolean;
  error: string | null;
}

export const useWantedPersonStatus = (
  personId?: string,
): UseWantedPersonStatusReturn => {
  const [isWanted, setIsWanted] = useState(false);
  const [wantedRecords, setWantedRecords] = useState<IWantedPerson[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkWantedStatus = async () => {
      if (!personId) {
        setIsWanted(false);
        setWantedRecords([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const filterObj = {
          operator: 'and',
          items: [
            {
              field: 'persons.masterPerson',
              operator: 'equals',
              value: personId,
            },
          ],
        };

        const response = await WantedPersonApi.getWantedPersonList({
          filter: JSON.stringify(filterObj),
        });

        const records = response.data?.results || [];

        setWantedRecords(records);
        setIsWanted(records.length > 0);
      } catch (err: any) {
        setError(err.message || 'Failed to check wanted person status');
        setIsWanted(false);
        setWantedRecords([]);
      } finally {
        setLoading(false);
      }
    };

    checkWantedStatus();
  }, [personId]);

  return {
    isWanted,
    wantedRecords,
    loading,
    error,
  };
};
